/**
 * Opens one meeting on request (TRUS-691): a `?meeting=ID` link (staff
 * /workspace-messaging?meeting=ID, member /member/inbox?meeting=ID — what the
 * invites and pushes link to), a clicked `meeting_invite` notification, or a
 * row in the upcoming list. It shows the meeting's conversation (when the
 * meeting has one) and the meeting card in a dialog, from where Join, Add to
 * calendar and RSVP work as on the card in the conversation.
 *
 * Mounted by the messaging workspace (staff) and the member inbox.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { isInAnyPopout } from '@/lib/popout';
import { PUSH_OPEN_MEETING_EVENT } from '@/lib/pushNavigation';
import { meetingIdFromSearch, type Meeting } from '@/services/meetingsApi';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { callStore } from '../calls/callStore';
import { MeetingCard } from './MeetingCard';
import { GuestLinkSlot } from './meetingSlots';
import { meetingStore, useRequestedMeetingId } from './meetingStore';
import { meetingKey, useMeeting, useMeetingsApi } from './useMeetings';

export { meetingStore };


interface MeetingsHostProps {
  currentUserId?: number | null;
  currentMemberId?: number | null;
}

export function MeetingsHost({ currentUserId, currentMemberId }: MeetingsHostProps) {
  const { t } = useAppTranslation();
  const api = useMeetingsApi();
  const queryClient = useQueryClient();
  const requestedId = useRequestedMeetingId();
  const [shownId, setShownId] = useState<number | null>(null);
  const handled = useRef(false);

  // ?meeting=ID, once (pop-outs carry their own state).
  useEffect(() => {
    if (handled.current || isInAnyPopout()) return;
    const id = meetingIdFromSearch(window.location.search);
    if (id == null) return;
    handled.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('meeting');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      /* ignore */
    }
    meetingStore.open(id);
  }, []);

  // A meeting notification clicked while the app is open.
  useEffect(() => {
    const onPush = (e: Event) => {
      const id = Number((e as CustomEvent<{ meetingId?: unknown }>).detail?.meetingId);
      if (Number.isInteger(id) && id > 0) meetingStore.open(id);
    };
    window.addEventListener(PUSH_OPEN_MEETING_EVENT, onPush);
    return () => window.removeEventListener(PUSH_OPEN_MEETING_EVENT, onPush);
  }, []);

  const openMeeting = useCallback(
    async (id: number) => {
      let m: Meeting;
      try {
        m = await queryClient.fetchQuery({ queryKey: meetingKey(api.kind, id), queryFn: () => api.get(id), staleTime: 5_000 });
      } catch {
        toast.error(t('messaging.meetings.linkUnavailable', "This meeting isn't available to you, or it no longer exists."));
        meetingStore.close();
        return;
      }
      if (m.channel_id != null) callStore.requestChannel(m.channel_id);
      setShownId(m.id);
    },
    [api, queryClient, t],
  );

  useEffect(() => {
    if (requestedId == null) return;
    void openMeeting(requestedId);
  }, [requestedId, openMeeting]);

  const q = useMeeting(shownId, shownId != null);
  const close = () => {
    setShownId(null);
    meetingStore.close();
  };

  return (
    <Dialog open={shownId != null && !!q.data} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="meeting-dialog">
        <DialogHeader>
          <DialogTitle>{q.data?.title || t('messaging.meetings.untitled', 'Meeting')}</DialogTitle>
          <DialogDescription>
            {q.data?.channel_name
              ? t('messaging.meetings.inConversation', 'In {{name}}', { name: q.data.channel_name })
              : t('messaging.meetings.standalone', 'A meeting outside any conversation')}
          </DialogDescription>
        </DialogHeader>
        {q.data && (
          <MeetingCard
            meeting={q.data}
            currentUserId={currentUserId}
            currentMemberId={currentMemberId}
            footer={<GuestLinkSlot meeting={q.data} />}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MeetingsHost;
